import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
	get_page,
	get_page_sections,
	create_section,
	update_section,
	delete_section,
	get_page_images,
	create_image,
	update_image,
	delete_image,
	get_page_documents,
	create_document,
	update_document,
	delete_document,
} from "../../api/functions";
import { useApi } from "../../api/api";
import { LoadingSpinner, AdminEndpoint } from "../../components";

function localizedTitle(item) {
	return item?.title_cz || item?.title_en || item?.title_de || "Untitled";
}

function getSectionFields(t) {
	return [
		{
			type: "language_tabs",
			name: "content_tabs",
			languages: ["en", "cz", "de"],
			fields: [
				{
					name: "title",
					label: t("admin.fields.title"),
					type: "text",
					required: false,
					placeholder: t("admin.placeholders.sectionTitle"),
				},
				{
					name: "text",
					label: t("admin.fields.content"),
					type: "markdown",
					required: false,
					placeholder: t("admin.placeholders.sectionContent"),
				},
			],
		},
		{
			name: "private",
			label: t("admin.fields.membersOnly"),
			type: "boolean",
			defaultValue: false,
		},
	];
}

function getImageFields(t, currentFilename) {
	return [
		{
			type: "language_tabs",
			name: "content_tabs",
			languages: ["en", "cz", "de"],
			fields: [
				{
					name: "title",
					label: t("admin.fields.title"),
					type: "text",
					required: false,
					placeholder: t("admin.placeholders.imageTitle"),
				},
			],
		},
		{
			name: "private",
			label: t("admin.fields.membersOnly"),
			type: "boolean",
			defaultValue: false,
		},
		{
			name: "image",
			label: t("admin.fields.imageFile"),
			type: "file",
			accept: "image/*",
			currentFile: currentFilename || null,
		},
	];
}

function getDocumentFields(t, currentFilename) {
	return [
		{
			type: "language_tabs",
			name: "content_tabs",
			languages: ["en", "cz", "de"],
			fields: [
				{
					name: "title",
					label: t("admin.fields.title"),
					type: "text",
					required: false,
					placeholder: t("admin.placeholders.documentTitle"),
				},
			],
		},
		{
			name: "private",
			label: t("admin.fields.membersOnly"),
			type: "boolean",
			defaultValue: false,
		},
		{
			name: "document",
			label: t("admin.fields.documentFile"),
			type: "file",
			accept: ".pdf,.doc,.docx",
			currentFile: currentFilename || null,
		},
	];
}

// ─── Entity section ───────────────────────────────────────────────────────────

function EntitySection({
	label,
	singularLabel,
	items,
	entityType,
	pageId,
	onRefresh,
	createApi,
	updateApi,
	deleteApi,
}) {
	const { t } = useTranslation();
	const [mode, setMode] = useState("list"); // 'list' | 'add' | 'edit'
	const [editingItem, setEditingItem] = useState(null);

	const getFields = () => {
		if (entityType === "section") return getSectionFields(t);
		if (entityType === "image")
			return getImageFields(t, editingItem?.image_filename);
		if (entityType === "document")
			return getDocumentFields(t, editingItem?.document_filename);
		return [];
	};

	const handleAdd = () => {
		setEditingItem(null);
		setMode("add");
	};

	const handleEdit = (item) => {
		setEditingItem(item);
		setMode("edit");
	};

	const handleCancel = () => {
		setMode("list");
		setEditingItem(null);
	};

	const handleDelete = async (id) => {
		if (
			!window.confirm(
				t("admin.pageDetail.deleteConfirm", {
					item: singularLabel.toLowerCase(),
				}),
			)
		)
			return;
		try {
			const payload =
				entityType === "section"
					? { section_id: id }
					: entityType === "image"
						? { image_id: id }
						: { document_id: id };
			await deleteApi.execute(payload);
			toast.success(
				t("admin.pageDetail.deletedSuccess", { item: singularLabel }),
			);
			if (editingItem?.id === id) handleCancel();
			onRefresh();
		} catch {
			toast.error(
				t("admin.pageDetail.failedDelete", {
					item: singularLabel.toLowerCase(),
				}),
			);
		}
	};

	const handleSubmit = async (formData) => {
		try {
			if (mode === "add") {
				await createApi.execute({ ...formData, page_id: pageId });
				toast.success(
					t("admin.pageDetail.createdSuccess", { item: singularLabel }),
				);
			} else {
				const idField =
					entityType === "section"
						? "section_id"
						: entityType === "image"
							? "image_id"
							: "document_id";
				await updateApi.execute({ ...formData, [idField]: editingItem.id });
				toast.success(
					t("admin.pageDetail.updatedSuccess", { item: singularLabel }),
				);
			}
			handleCancel();
			onRefresh();
		} catch {
			toast.error(
				t("admin.pageDetail.failedSave", { item: singularLabel.toLowerCase() }),
			);
		}
	};

	// ── Edit / Add mode ─────────────────────────────────────────────────────────
	if (mode === "add" || mode === "edit") {
		return (
			<div className="page-detail-entity-section">
				<div
					className="tab-header"
					style={{ marginBottom: "var(--spacing-lg, 24px)" }}
				>
					<h3 className="page-detail-entity-title">
						{mode === "add"
							? `${t("admin.pageDetail.add")} ${singularLabel}`
							: `${t("admin.pageDetail.edit")} ${singularLabel}`}
					</h3>
				</div>

				<div className="edit-actions-top">
					<button className="cancel-btn-top" onClick={handleCancel}>
						← {t("admin.pageDetail.backTo")} {label}
					</button>
					{mode === "edit" && editingItem && (
						<button
							className="delete-btn"
							onClick={() => handleDelete(editingItem.id)}
							disabled={deleteApi.loading}
						>
							{deleteApi.loading
								? t("common.deleting")
								: `${t("admin.pageDetail.delete")} ${singularLabel}`}
						</button>
					)}
				</div>

				<AdminEndpoint
					fields={getFields()}
					onSubmit={handleSubmit}
					onCancel={null}
					initialData={editingItem || {}}
					submitLabel={
						mode === "add"
							? `${t("admin.pageDetail.create")} ${singularLabel}`
							: `${t("admin.pageDetail.update")} ${singularLabel}`
					}
					isLoading={createApi.loading || updateApi.loading}
				/>
			</div>
		);
	}

	// ── List mode ───────────────────────────────────────────────────────────────
	return (
		<div className="page-detail-entity-section">
			<div className="page-detail-entity-header">
				<h3 className="page-detail-entity-title">
					{label}
					<span className="page-detail-entity-count">{items.length}</span>
				</h3>
				<button className="create-btn create-btn-sm" onClick={handleAdd}>
					<svg
						width="14"
						height="14"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							d="M12 4v16m8-8H4"
						/>
					</svg>
					{t("admin.pageDetail.add")} {singularLabel}
				</button>
			</div>

			{items.length === 0 && (
				<p className="page-detail-empty">
					{t("admin.pageDetail.noItems", { label: label.toLowerCase() })}
				</p>
			)}

			<div className="page-detail-item-list">
				{items.map((item) => (
					<div key={item.id} className="page-detail-item-row">
						<div className="page-detail-item-info">
							<span className="page-detail-item-title">
								{localizedTitle(item)}
							</span>
							{(item.document_filename || item.image_filename) && (
								<span className="page-detail-item-filename">
									{item.document_filename || item.image_filename}
								</span>
							)}
							{item.private && (
								<span className="item-badge">{t("common.membersOnly")}</span>
							)}
						</div>
						<div className="page-detail-item-actions">
							<button
								className="item-copy-btn"
								onClick={() => handleEdit(item)}
							>
								{t("admin.pageDetail.edit")}
							</button>
							<button
								className="item-delete-btn"
								onClick={() => handleDelete(item.id)}
							>
								{t("admin.pageDetail.delete")}
							</button>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

// ─── Page Detail View ─────────────────────────────────────────────────────────

const PageDetailView = ({ pageId }) => {
	const navigate = useNavigate();
	const { t, i18n } = useTranslation();

	const [page, setPage] = useState(null);
	const [sections, setSections] = useState([]);
	const [images, setImages] = useState([]);
	const [documents, setDocuments] = useState([]);
	const [isLoading, setIsLoading] = useState(true);

	const getPageApi = useApi(get_page);
	const getPageSectionsApi = useApi(get_page_sections);
	const getPageImagesApi = useApi(get_page_images);
	const getPageDocumentsApi = useApi(get_page_documents);
	const createSectionApi = useApi(create_section);
	const updateSectionApi = useApi(update_section);
	const deleteSectionApi = useApi(delete_section);
	const createImageApi = useApi(create_image);
	const updateImageApi = useApi(update_image);
	const deleteImageApi = useApi(delete_image);
	const createDocumentApi = useApi(create_document);
	const updateDocumentApi = useApi(update_document);
	const deleteDocumentApi = useApi(delete_document);

	const fetchAll = useCallback(async () => {
		setIsLoading(true);
		try {
			const [pageData, sectionsData, imagesData, docsData] = await Promise.all([
				getPageApi.execute({ page_id: parseInt(pageId) }),
				getPageSectionsApi
					.execute({ page_id: parseInt(pageId) })
					.catch(() => []),
				getPageImagesApi.execute({ page_id: parseInt(pageId) }).catch(() => []),
				getPageDocumentsApi
					.execute({ page_id: parseInt(pageId) })
					.catch(() => []),
			]);
			setPage(pageData);
			setSections(sectionsData || []);
			setImages(imagesData || []);
			setDocuments(docsData || []);
		} finally {
			setIsLoading(false);
		}
	}, [pageId]);

	useEffect(() => {
		fetchAll();
	}, [fetchAll]);

	const refreshSections = useCallback(
		() =>
			getPageSectionsApi
				.execute({ page_id: parseInt(pageId) })
				.then((d) => setSections(d || [])),
		[pageId],
	);
	const refreshImages = useCallback(
		() =>
			getPageImagesApi
				.execute({ page_id: parseInt(pageId) })
				.then((d) => setImages(d || [])),
		[pageId],
	);
	const refreshDocuments = useCallback(
		() =>
			getPageDocumentsApi
				.execute({ page_id: parseInt(pageId) })
				.then((d) => setDocuments(d || [])),
		[pageId],
	);

	const getPageTitle = () => {
		if (!page) return "";
		const lang = i18n.language === "cs" ? "cz" : i18n.language;
		return (
			page[`title_${lang}`] ||
			page.title_cz ||
			page.title_en ||
			page.title_de ||
			"Untitled"
		);
	};

	if (isLoading) {
		return (
			<div className="tab-container">
				<LoadingSpinner message={t("admin.pageDetail.loading")} />
			</div>
		);
	}

	if (!page) {
		return (
			<div className="tab-container">
				<p>{t("admin.pageDetail.notFound")}</p>
			</div>
		);
	}

	return (
		<div className="tab-container">
			{/* Header */}
			<div className="page-detail-top">
				<button
					className="cancel-btn-top"
					onClick={() => navigate("/admin/infopages")}
				>
					← {t("admin.pageDetail.backToPages")}
				</button>
				<div className="page-detail-title-row">
					<div className="page-detail-title-group">
						<span className="item-id">#{page.id}</span>
						<h2 className="tab-title" style={{ marginBottom: 0 }}>
							{getPageTitle()}
						</h2>
					</div>
					<div className="page-detail-title-actions">
						{page.private && (
							<span className="item-badge">{t("common.membersOnly")}</span>
						)}
						<button
							className="create-btn"
							onClick={() => navigate(`/admin/infopages/${pageId}/edit`)}
						>
							{t("admin.pageDetail.editPage")}
						</button>
					</div>
				</div>
			</div>

			{/* Sections */}
			<EntitySection
				label={t("admin.pageDetail.sections")}
				singularLabel={t("admin.pageDetail.section")}
				items={sections}
				entityType="section"
				pageId={parseInt(pageId)}
				onRefresh={refreshSections}
				createApi={createSectionApi}
				updateApi={updateSectionApi}
				deleteApi={deleteSectionApi}
			/>

			{/* Images */}
			<EntitySection
				label={t("admin.pageDetail.images")}
				singularLabel={t("admin.pageDetail.image")}
				items={images}
				entityType="image"
				pageId={parseInt(pageId)}
				onRefresh={refreshImages}
				createApi={createImageApi}
				updateApi={updateImageApi}
				deleteApi={deleteImageApi}
			/>

			{/* Documents */}
			<EntitySection
				label={t("admin.pageDetail.documents")}
				singularLabel={t("admin.pageDetail.document")}
				items={documents}
				entityType="document"
				pageId={parseInt(pageId)}
				onRefresh={refreshDocuments}
				createApi={createDocumentApi}
				updateApi={updateDocumentApi}
				deleteApi={deleteDocumentApi}
			/>
		</div>
	);
};

export default PageDetailView;
